import { useLingui } from "@lingui/react/macro";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from "@orbin/ui-web";
import { Kbd, KbdGroup } from "@orbin/ui-web/components/ui/kbd";
import { useState } from "react";
import { CLIENT_BUILD } from "../lib/client-build";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform);

/** Feedback becomes a support ticket whose subject starts with [Feedback]. */
export function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const [body, setBody] = useState("");
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function change(next: boolean) {
    onOpenChange(next);
    if (!next) {
      setBody("");
      setSent(false);
      setError(null);
    }
  }

  async function send() {
    if (pending || !body.trim()) return;
    setPending(true);
    setError(null);
    try {
      const first = body.trim().split("\n")[0] ?? "";
      await rpc.support.createTicket({ subject: `[Feedback] ${first.slice(0, 80)}`, body });
      setSent(true);
    } catch (cause) {
      setError(errorText(cause, t`Could not send your message`));
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="sm:max-w-md" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>{t`Send feedback`}</DialogTitle>
          <DialogDescription className="sr-only">{t`Tell us what to improve`}</DialogDescription>
        </DialogHeader>
        {sent ? (
          <p role="status" className="text-[14px] text-foreground">
            {t`Thank you. We read every message.`}
          </p>
        ) : (
          <>
            <Textarea
              aria-label={t`Feedback`}
              placeholder={t`What works, what doesn't, what is missing?`}
              rows={5}
              maxLength={10_000}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              autoFocus
            />
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </>
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => change(false)}>
            {t`Close`}
          </Button>
          {sent ? null : (
            <Button disabled={pending || !body.trim()} onClick={() => void send()}>
              {t`Send`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The shortcuts the app registers, shown with the platform's modifier. */
export function ShortcutsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const mod = mac ? "⌘" : "Ctrl";
  const rows: Array<{ keys: string[]; label: string }> = [
    { keys: [mod, "K"], label: t`Open the command menu` },
    { keys: [mod, "N"], label: t`Open the create menu (desktop app)` },
    { keys: [mod, "1–9"], label: t`Jump to a bot in the command menu` },
    { keys: ["Enter"], label: t`Send the message` },
    { keys: ["Shift", "Enter"], label: t`New line` },
    { keys: ["Esc"], label: t`Close the open panel or window` },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="shortcuts-dialog">
        <DialogHeader>
          <DialogTitle>{t`Keyboard shortcuts`}</DialogTitle>
          <DialogDescription className="sr-only">{t`Keyboard shortcuts`}</DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col divide-y divide-border">
          {rows.map((row) => (
            <li key={row.label} className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-[14px] text-foreground">{row.label}</span>
              <KbdGroup>
                {row.keys.map((key) => (
                  <Kbd key={key}>{key}</Kbd>
                ))}
              </KbdGroup>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

/** Version, terms, privacy. */
export function AboutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="about-dialog">
        <DialogHeader>
          <DialogTitle>{t`About Preá`}</DialogTitle>
          <DialogDescription>{t`Version ${CLIENT_BUILD || "dev"}`}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1 text-[14px]">
          <a
            className="text-foreground hover:underline"
            href="https://prea.ia.br/termos/"
            target="_blank"
            rel="noreferrer"
          >
            {t`Terms of use`}
          </a>
          <a
            className="text-foreground hover:underline"
            href="https://prea.ia.br/privacidade/"
            target="_blank"
            rel="noreferrer"
          >
            {t`Privacy policy`}
          </a>
        </div>
        <p className="text-[12.5px] text-muted-foreground">© {new Date().getFullYear()} Orbin</p>
      </DialogContent>
    </Dialog>
  );
}
